import React from 'react';
import { generatePath, Link } from 'react-router-dom';
import Tooltip from '@mui/material/Tooltip';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import Loader from '@/components/common/Loader';
import ConstituentType from '@/constants/ConstituentType';
import ContributionType from '@/constants/ContributionType';
import DateType from '@/constants/DateType';
import ReportType from '@/constants/ReportType';
import AuthRoutes from '@/constants/routing/AuthRoutes';
import {
  START_OF_1900_API_FORMAT,
  TODAY_API_FORMAT,
} from '@/lib/constants/dateConstants';
import translate from '@/lib/intl/translate';
import ConstituentPropType from '@/lib/types/ConstituentPropType';
import { formatCurrency } from '@/utils/currency';
import { formatDonationTime } from '@/utils/donationTime';
import joinNonEmptyParams from '@/utils/joinNonEmptyParams';
import recursiveObjectValues from '@/utils/recursiveObjectValues';
import useConstituentGivingSummaries from '../hooks/useConstituentGivingSummaries';

const ZERO_DOLLARS = '$0';
const ZERO_HOURS = translate('X_HOURS', { hours: 0 });

function GivingSummary({
  givingSummaryData,
  plannedGivingData,
  softCreditData,
  title,
}) {
  const renderItem = (item) => {
    const itemNotZero =
      item.amount !== ZERO_DOLLARS && item.amount !== ZERO_HOURS;
    return (
      <>
        <div className="text-base">{item.title}</div>
        {itemNotZero && item.linkParams ? (
          <Link
            className="text-tertiary-main"
            to={{
              pathname: generatePath(AuthRoutes.REPORTS),
              search: joinNonEmptyParams(item.linkParams),
            }}
          >
            <div className="text-xl font-normal text-tertiary-main">
              {item.amount}
            </div>
          </Link>
        ) : (
          <div className="text-xl font-normal">{item.amount}</div>
        )}
        <div className="text-sm font-normal capitalize text-grey-900">
          {item.count !== undefined ? `${item.count} ` : ''}
          {item.subTitle}
        </div>
        {item.details && (
          <div className="pt-2 text-xs font-normal lowercase text-grey-900">
            {item.details}
          </div>
        )}
      </>
    );
  };

  return (
    <div className="pb-6">
      <div className="pb-6">{title}</div>
      <div className="flex flex-col lg:flex-row">
        <div className="flex w-full flex-wrap lg:w-2/3">
          {(givingSummaryData || []).map((item) =>
            item.tooltip ? (
              <Tooltip title={item.tooltip} key={item.title}>
                <div
                  className={classNames('w-1/2 pb-6 pr-2 sm:w-1/3', item.order)}
                >
                  {renderItem(item)}
                </div>
              </Tooltip>
            ) : (
              <div
                className={classNames('w-1/2 pb-6 pr-2 sm:w-1/3', item.order)}
                key={item.title}
              >
                {renderItem(item)}
              </div>
            ),
          )}
        </div>
        <div className="ml-0 w-full items-start border-0 pl-0 lg:flex lg:w-1/3 lg:border-l lg:border-grey-300 lg:pl-6 xl:pl-14">
          {plannedGivingData && (
            <div className="flex flex-wrap">
              {plannedGivingData.map((item, idx) => (
                <div
                  className={classNames('w-1/2 pb-6 sm:w-1/3 lg:w-full', {
                    'lg:pb-0': idx,
                  })}
                  key={item.key || item.title}
                >
                  {renderItem(item)}
                </div>
              ))}
            </div>
          )}
          {softCreditData && (
            <div className="flex flex-wrap">
              {softCreditData.map((item, idx) => (
                <div
                  className={classNames('w-1/2 pb-6 sm:w-1/3 lg:w-full', {
                    'lg:pb-0': idx,
                  })}
                  key={item.key || item.title}
                >
                  {renderItem(item)}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

const GivingSummaryItems = PropTypes.arrayOf(
  PropTypes.shape({
    amount: PropTypes.string,
    count: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    linkParams: PropTypes.shape({
      constituentIds: PropTypes.string,
      dateType: PropTypes.string,
      donationTypes: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.arrayOf(PropTypes.string),
      ]),
      endDate: PropTypes.string,
      reportType: PropTypes.string,
      softCreditIds: PropTypes.string,
      startDate: PropTypes.string,
    }),
    order: PropTypes.string,
    subTitle: PropTypes.string,
    title: PropTypes.string,
    tooltip: PropTypes.node,
  }),
);

GivingSummary.propTypes = {
  givingSummaryData: GivingSummaryItems,
  plannedGivingData: GivingSummaryItems,
  softCreditData: GivingSummaryItems,
  title: PropTypes.string.isRequired,
};

GivingSummary.defaultProps = {
  givingSummaryData: null,
  plannedGivingData: null,
  softCreditData: null,
};

function ConstituentGivingSummaries({ constituent }) {
  const { constituentId, constituentType, employees, household } = constituent;
  const isPerson = constituentType === ConstituentType.PERSON;
  const isOrganization = constituentType === ConstituentType.ORG;
  const hasHousehold = household?.length > 0;
  const hasEmployees = employees?.length > 0;

  const { data } = useConstituentGivingSummaries(constituentId);

  const hasContributed = React.useMemo(() => {
    if (!data) return false;
    // If the constituent has not made a contribution for any given type, the
    // value will be `null` for that type. Here we want to see if the user has
    // made any contributions whatsoever, so we're taking their giving summary
    // object and flattening out the values to check if any are not null.
    return recursiveObjectValues({
      constituent: data.constituent,
      group: data.group,
    }).some((val) => val !== null);
  }, [data]);

  const items = React.useMemo(() => {
    if (!(data || hasContributed)) return null;

    const constituentGivingSummaryData = [
      {
        amount: formatCurrency(data.constituent?.thisYear.sum, true),
        count: data.constituent?.thisYear.count ?? 0,
        linkParams: {
          constituentIds: constituentId,
          dateType: DateType.CALENDAR_YTD,
          donationTypes: [
            ContributionType.GENERAL,
            ContributionType.RECURRING,
            ContributionType.RESTRICTED,
            ContributionType.IN_KIND,
            ContributionType.TIME,
            ContributionType.GRANT,
            ContributionType.SPONSORSHIP,
            ContributionType.TICKET,
          ],
          reportType: ReportType.DONATIONS,
        },
        order: 'order-1 sm:order-1',
        subTitle: translate('DONOR_CONTRIBUTIONS'),
        title: translate('DONOR_YTD_CONTRIBUTIONS'),
        tooltip: (
          <>
            <div>
              {translate('DONATIONS')}:{' '}
              {formatCurrency(data.constituent?.thisYear?.donationSum, true)}
            </div>
            <div>
              {translate('EARNED_REVENUE')}:{' '}
              {formatCurrency(
                data.constituent?.thisYear?.earnedRevenueSum,
                true,
              )}
            </div>
          </>
        ),
      },
      {
        amount: formatCurrency(data.constituent?.thisYear.inKind, true),
        count: data.constituent?.thisYear.inKindCount,
        linkParams: {
          constituentIds: constituentId,
          dateType: DateType.CALENDAR_YTD,
          donationTypes: ContributionType.IN_KIND,
          reportType: ReportType.DONATIONS,
        },
        order: 'order-3 sm:order-2',
        subTitle: translate('DONOR_CONTRIBUTIONS'),
        title: translate('DONOR_YTD_IN_KIND'),
      },
      {
        amount: formatDonationTime(data.constituent?.thisYear.minutes),
        count: data.constituent?.thisYear.minutesCount,
        linkParams: {
          constituentIds: constituentId,
          dateType: DateType.CALENDAR_YTD,
          donationTypes: ContributionType.TIME,
          reportType: ReportType.DONATIONS,
        },
        order: 'order-5 sm:order-3',
        subTitle: translate('DONOR_CONTRIBUTIONS'),
        title: translate('DONOR_YTD_HOURS'),
      },
      {
        amount: formatCurrency(data.constituent?.lifetime.sum, true),
        count: data.constituent?.lifetime.count,
        linkParams: {
          constituentIds: constituentId,
          dateType: DateType.ALL_TIME,
          donationTypes: [
            ContributionType.GENERAL,
            ContributionType.RECURRING,
            ContributionType.RESTRICTED,
            ContributionType.IN_KIND,
            ContributionType.TIME,
            ContributionType.GRANT,
            ContributionType.SPONSORSHIP,
            ContributionType.TICKET,
          ],
          endDate: TODAY_API_FORMAT,
          reportType: ReportType.DONATIONS,
          startDate: START_OF_1900_API_FORMAT,
        },
        order: 'order-2 sm:order-4',
        subTitle: translate('DONOR_CONTRIBUTIONS'),
        title: translate('DONOR_CONTRIBUTIONS_LIFETIME_CONTRIBUTIONS'),
        tooltip: (
          <>
            <div>
              {translate('CONTRIBUTIONS')}:{' '}
              {formatCurrency(data.constituent?.lifetime?.donationSum, true)}
            </div>
            <div>
              {translate('EARNED_REVENUE')}:{' '}
              {formatCurrency(
                data.constituent?.lifetime?.earnedRevenueSum,
                true,
              )}
            </div>
          </>
        ),
      },
      {
        amount: formatCurrency(data.constituent?.lifetime.inKind, true),
        count: data.constituent?.lifetime.inKindCount,
        linkParams: {
          constituentIds: constituentId,
          dateType: DateType.ALL_TIME,
          donationTypes: ContributionType.IN_KIND,
          endDate: TODAY_API_FORMAT,
          reportType: ReportType.DONATIONS,
          startDate: START_OF_1900_API_FORMAT,
        },
        order: 'order-4 sm:order-5',
        subTitle: translate('DONOR_CONTRIBUTIONS'),
        title: translate('DONOR_DONATIONS_LIFETIME_INKIND'),
      },
      {
        amount: formatDonationTime(data.constituent?.lifetime.minutes),
        count: data.constituent?.lifetime.minutesCount,
        linkParams: {
          constituentIds: constituentId,
          dateType: DateType.ALL_TIME,
          donationTypes: ContributionType.TIME,
          endDate: TODAY_API_FORMAT,
          reportType: ReportType.DONATIONS,
          startDate: START_OF_1900_API_FORMAT,
        },
        order: 'order-6 sm:order-6',
        subTitle: translate('DONOR_CONTRIBUTIONS'),
        title: translate('DONOR_CONTRIBUTIONS_LIFETIME_TIME'),
      },
    ];

    const constituentPlannedGivingData = [
      {
        amount: formatCurrency(
          data.constituent?.plannedGiving.pledgesSum,
          true,
        ),
        count: formatCurrency(
          data.constituent?.plannedGiving.pledgesReceivedSum,
          true,
        ),
        linkParams: {
          constituentIds: constituentId,
          dateType: DateType.ALL_TIME,
          donationTypes: ContributionType.PLEDGE,
          endDate: TODAY_API_FORMAT,
          reportType: ReportType.DONATIONS,
          startDate: START_OF_1900_API_FORMAT,
        },
        subTitle: translate('REPORTS_PLEDGE_RECEIVED'),
        title: translate('DONATION_PLEDGES'),
      },
      {
        amount: formatCurrency(data.constituent?.plannedGiving.giftsSum, true),
        linkParams: {
          constituentIds: constituentId,
          dateType: DateType.ALL_TIME,
          donationTypes: ContributionType.PLANNED_GIVING,
          endDate: TODAY_API_FORMAT,
          reportType: ReportType.DONATIONS,
          startDate: START_OF_1900_API_FORMAT,
        },
        title: translate('GIVING_PLANNED_GIVING'),
      },
    ];

    const softCreditData = [
      {
        amount: formatCurrency(data.constituent?.softCredits.sum, true),
        linkParams: {
          dateType: DateType.ALL_TIME,
          endDate: TODAY_API_FORMAT,
          reportType: ReportType.DONATIONS,
          softCreditIds: constituentId,
          startDate: START_OF_1900_API_FORMAT,
        },
        subTitle: translate('CONSTITUENT_TOTAL_MONETARY_CONTRIBUTED'),
        title: translate('SOFT_CREDITS'),
        key: 'soft-credit-money',
      },
      {
        amount: formatDonationTime(data.constituent?.softCredits.minutes, true),
        linkParams: {
          dateType: DateType.ALL_TIME,
          donationTypes: ContributionType.TIME,
          endDate: TODAY_API_FORMAT,
          reportType: ReportType.DONATIONS,
          softCreditIds: constituentId,
          startDate: START_OF_1900_API_FORMAT,
        },
        subTitle: translate('CONSTITUENT_TOTAL_TIME_CONTRIBUTED'),
        title: translate('SOFT_CREDITS'),
        key: 'soft-credit-time',
      },
    ];

    const groupGivingSummaryData = [
      {
        amount: formatCurrency(data.group?.thisYear.sum, true),
        count: data.group?.thisYear.count,
        order: 'order-1 sm:order-1',
        subTitle: translate('DONOR_CONTRIBUTIONS'),
        title: translate('DONOR_YTD_CONTRIBUTIONS'),
        tooltip: (
          <>
            <div>
              {translate('CONTRIBUTIONS')}:{' '}
              {formatCurrency(data.group?.thisYear?.donationSum, true)}
            </div>
            <div>
              {translate('EARNED_REVENUE')}:{' '}
              {formatCurrency(data.group?.thisYear?.earnedRevenueSum, true)}
            </div>
          </>
        ),
      },
      {
        amount: formatCurrency(data.group?.thisYear.inKind, true),
        count: data.group?.thisYear.inKindCount,
        order: 'order-3 sm:order-2',
        subTitle: translate('DONOR_CONTRIBUTIONS'),
        title: translate('DONOR_YTD_IN_KIND'),
      },
      {
        amount: formatDonationTime(data.group?.thisYear.minutes),
        count: data.group?.thisYear.minutesCount,
        order: 'order-5 sm:order-3',
        subTitle: translate('DONOR_CONTRIBUTIONS'),
        title: translate('DONOR_YTD_HOURS'),
      },
      {
        amount: formatCurrency(data.group?.lifetime.sum, true),
        count: data.group?.lifetime.count,
        order: 'order-2 sm:order-4',
        subTitle: translate('DONOR_CONTRIBUTIONS'),
        title: translate('DONOR_CONTRIBUTIONS_LIFETIME_CONTRIBUTIONS'),
        tooltip: (
          <>
            <div>
              {translate('CONTRIBUTIONS')}:{' '}
              {formatCurrency(data.group?.lifetime?.donationSum, true)}
            </div>
            <div>
              {translate('EARNED_REVENUE')}:{' '}
              {formatCurrency(data.constituent?.group?.earnedRevenueSum, true)}
            </div>
          </>
        ),
      },
      {
        amount: formatCurrency(data.group?.lifetime.inKind, true),
        count: data.group?.lifetime.inKindCount,
        order: 'order-4 sm:order-5',
        subTitle: translate('DONOR_CONTRIBUTIONS'),
        title: translate('DONOR_DONATIONS_LIFETIME_INKIND'),
      },
      {
        amount: formatDonationTime(data.group?.lifetime.minutes),
        count: data.group?.lifetime.minutesCount,
        order: 'order-6 sm:order-6',
        subTitle: translate('DONOR_CONTRIBUTIONS'),
        title: translate('DONOR_CONTRIBUTIONS_LIFETIME_TIME'),
      },
    ];

    const groupPlannedGivingData = [
      {
        amount: formatCurrency(data.group?.plannedGiving.sum, true),
        count: formatCurrency(
          data.group?.plannedGiving.pledgesReceivedSum,
          true,
        ),
        subTitle: translate('REPORTS_PLEDGE_RECEIVED'),
        title: translate('DONATION_PLEDGES'),
      },
      {
        amount: formatCurrency(data.group?.plannedGiving.giftsSum, true),
        title: translate('GIVING_PLANNED_GIVING'),
      },
    ];
    return {
      constituentGivingSummaryData,
      constituentPlannedGivingData,
      softCreditData,
      groupGivingSummaryData,
      groupPlannedGivingData,
    };
  }, [data, hasContributed]);

  if (!data) return <Loader className="my-8" isCentered />;
  if (!hasContributed) return null;

  const {
    constituentGivingSummaryData,
    constituentPlannedGivingData,
    softCreditData,
    groupGivingSummaryData,
    groupPlannedGivingData,
  } = items;

  return (
    <div className="max-w-6xl p-4 pb-0 type-h3 lg:px-8 lg:pt-6 lg:pb-6">
      <GivingSummary
        givingSummaryData={constituentGivingSummaryData}
        plannedGivingData={constituentPlannedGivingData}
        softCreditData={softCreditData}
        title={
          isOrganization
            ? translate('GIVING_SUMMARY_ORGANIZATION')
            : translate('GIVING_SUMMARY_INDIVIDUAL')
        }
      />
      {((isPerson && hasHousehold) || (isOrganization && hasEmployees)) &&
        data.group && (
          <GivingSummary
            givingSummaryData={groupGivingSummaryData}
            plannedGivingData={groupPlannedGivingData}
            title={
              isPerson
                ? translate('GIVING_SUMMARY_HOUSEHOLD')
                : translate('GIVING_SUMMARY_EMPLOYEE')
            }
          />
        )}
    </div>
  );
}

ConstituentGivingSummaries.propTypes = {
  constituent: ConstituentPropType.isRequired,
};

export default ConstituentGivingSummaries;
